import { styled } from '@mui/material';
import VideoEvidenceMap from 'components/Grading/video/VideoEvidenceMap';
import { useVideoReview } from 'components/Grading/video/VideoReviewContext';
import VideoTranscript from 'components/Grading/video/VideoTranscript';
import { useViewport } from 'contexts/viewport';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Box from 'shell/components/Box';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import TextInput from 'shell/components/TextInput';
import { downloadStringAsTxt } from 'utils/helper';
import { findTranscriptMatches, normalizeVideoTranscript } from 'utils/videoGrading';

const VideoViewer = ({ highlights, mimeType, transcript: sourceTranscript, url }) => {
    const { t } = useTranslation('assignment');
    const { device } = useViewport();
    const review = useVideoReview();
    const location = useLocation();
    const containerRef = useRef(null);
    const videoRef = useRef(null);
    const videoAnchorRef = useRef(null);
    const pendingTime = useRef(null);
    const handledRequest = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackError, setPlaybackError] = useState(false);
    const [isFloating, setIsFloating] = useState(false);
    const [placeholderHeight, setPlaceholderHeight] = useState(null);
    const [search, setSearch] = useState('');
    const [activeMatchIndex, setActiveMatchIndex] = useState(0);
    const transcript = useMemo(() => normalizeVideoTranscript(sourceTranscript), [sourceTranscript]);
    const searchMatches = useMemo(() => findTranscriptMatches(transcript.text, search), [search, transcript.text]);
    const highlightRanges = useMemo(
        () =>
            (highlights || []).flatMap((highlight) => {
                const quote =
                    typeof highlight.value === 'string'
                        ? highlight.value
                        : highlight.value?.quote || highlight.value?.highlight;
                return findTranscriptMatches(transcript.text, quote);
            }),
        [highlights, transcript.text]
    );

    const seekToTime = useCallback((time) => {
        const video = videoRef.current;
        if (!video || !Number.isFinite(time) || time < 0) return;
        pendingTime.current = time;
        if (video.readyState < 1) return;
        if (Number.isFinite(video.duration) && time > video.duration) {
            pendingTime.current = null;
            return;
        }
        try {
            video.currentTime = time;
            pendingTime.current = null;
            setCurrentTime(time);
            setPlaybackError(false);
            video.play()?.catch(() => setPlaybackError(true));
        } catch {
            setPlaybackError(true);
        }
    }, []);

    const onLoadedMetadata = () => {
        const video = videoRef.current;
        setDuration(Number.isFinite(video.duration) ? video.duration : 0);
        if (pendingTime.current !== null) seekToTime(pendingTime.current);
    };

    useEffect(() => {
        if (containerRef.current) containerRef.current.scrollTop = 0;
        setCurrentTime(0);
        setDuration(0);
        setSearch('');
        setActiveMatchIndex(0);
        setPlaybackError(false);
        setIsFloating(false);
        pendingTime.current = null;
    }, [url]);

    const seekRequest = review?.seekRequest;
    useEffect(() => {
        if (!seekRequest || handledRequest.current === seekRequest.id) return;
        handledRequest.current = seekRequest.id;
        seekToTime(seekRequest.start);
    }, [seekRequest, seekToTime, url]);

    useEffect(() => {
        const anchor = videoAnchorRef.current;
        if (!anchor || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsFloating(!entry.isIntersecting);
                setPlaceholderHeight(entry.boundingClientRect.height);
            },
            { threshold: 0.1, root: containerRef.current }
        );
        observer.observe(anchor);
        return () => observer.disconnect();
    }, [url]);

    useEffect(() => setActiveMatchIndex(0), [search]);
    useEffect(() => {
        const element = containerRef.current?.querySelector(`[data-match-index="${activeMatchIndex}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [activeMatchIndex, searchMatches]);

    const nextMatch = () =>
        setActiveMatchIndex((index) => (searchMatches.length ? (index + 1) % searchMatches.length : 0));
    const previousMatch = () =>
        setActiveMatchIndex((index) =>
            searchMatches.length ? (index - 1 + searchMatches.length) % searchMatches.length : 0
        );
    const mode = location.pathname.split('/').find((part) => ['submissions', 'preview', 'grading'].includes(part));

    return (
        <Stack ref={containerRef} sx={{ height: '100%', overflow: 'auto', minWidth: 0 }}>
            <Box
                ref={videoAnchorRef}
                sx={(theme) => ({
                    flexShrink: 0,
                    minHeight: isFloating ? placeholderHeight : 'auto',
                    background: theme.palette.ensightful.neutral[800],
                })}
            >
                <CustomVideo
                    key={url}
                    ref={videoRef}
                    controls
                    playsInline
                    preload={'metadata'}
                    device={device}
                    isFloating={isFloating}
                    location={mode}
                    onLoadedMetadata={onLoadedMetadata}
                    onTimeUpdate={() => setCurrentTime(videoRef.current.currentTime)}
                    onError={() => setPlaybackError(true)}
                >
                    <source src={url} type={mimeType} />
                </CustomVideo>
            </Box>
            {playbackError && (
                <Text role={'status'} color={'error'} sx={{ p: 3 }}>
                    {t('video-grading.playback-error', 'Playback could not start. Try the video controls.')}
                </Text>
            )}
            {review && (
                <VideoEvidenceMap
                    key={url}
                    evidence={review.evidence}
                    criteria={review.criteria}
                    duration={duration}
                    currentTime={currentTime}
                    selectedCriterionId={review.selectedCriterionId}
                    onSeek={review.seekToEvidence}
                />
            )}
            <Stack spacing={4} sx={(theme) => ({ background: theme.palette.ensightful.shades[0], pt: 4 })}>
                <Stack
                    sx={{ px: 4 }}
                    direction={'row'}
                    alignItems={'center'}
                    useFlexGap
                    flexWrap={'wrap'}
                    justifyContent={'space-between'}
                    spacing={2}
                >
                    <Text component={'h2'} variant={'p2'} weight={'medium'}>
                        {t('video-grading.transcription', 'Transcription')}
                    </Text>
                    <Stack direction={'row'} alignItems={'center'} spacing={2} sx={{ maxWidth: '100%' }}>
                        <TextInput
                            sx={{ width: device === 'mobile' ? '100%' : 250 }}
                            inputProps={{ 'aria-label': t('video-grading.search-transcript', 'Search transcript') }}
                            disablePointerEventsOnIcon={false}
                            value={search}
                            placeholder={t('common:controls.search', 'Search')}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.shiftKey ? previousMatch() : nextMatch();
                                }
                            }}
                            endIcon={
                                search ? (
                                    <Stack direction={'row'} alignItems={'center'} spacing={1}>
                                        <Text variant={'p3'} color={'light'} sx={{ whiteSpace: 'nowrap' }}>
                                            {searchMatches.length ? activeMatchIndex + 1 : 0}/{searchMatches.length}
                                        </Text>
                                        <IconButton
                                            kind={'chevronUp'}
                                            size={'small'}
                                            variant={'square'}
                                            disabled={!searchMatches.length}
                                            aria-label={t(
                                                'grading-section.document-preview.actions.previous-match',
                                                'Previous Match'
                                            )}
                                            onClick={previousMatch}
                                        />
                                        <IconButton
                                            kind={'chevronDown'}
                                            size={'small'}
                                            variant={'square'}
                                            disabled={!searchMatches.length}
                                            aria-label={t(
                                                'grading-section.document-preview.actions.next-match',
                                                'Next Match'
                                            )}
                                            onClick={nextMatch}
                                        />
                                    </Stack>
                                ) : (
                                    <Icon kind={'search'} />
                                )
                            }
                        />
                        <IconButton
                            kind={'download'}
                            size={'medium'}
                            variant={'square'}
                            disabled={!transcript.text}
                            aria-label={t(
                                'grading-section.document-preview.actions.download-transcript',
                                'Download Transcript'
                            )}
                            onClick={() => {
                                let name =
                                    (url || '')
                                        .split('/')
                                        .at(-1)
                                        .split('?')[0]
                                        .replace(/\.[^.]*$/, '') || 'video';
                                try {
                                    name = decodeURIComponent(name);
                                } catch {
                                    /* Keep the original filename if encoding is invalid. */
                                }
                                downloadStringAsTxt(`${name.replace(/\s/g, '_')}_transcript.txt`, transcript.text);
                            }}
                        />
                    </Stack>
                </Stack>
                <VideoTranscript
                    transcript={transcript}
                    currentTime={currentTime}
                    matches={searchMatches}
                    highlights={highlightRanges}
                    activeMatchIndex={activeMatchIndex}
                    onSeek={seekToTime}
                />
            </Stack>
        </Stack>
    );
};

const CustomVideo = styled('video', {
    shouldForwardProp: (prop) => !['device', 'isFloating', 'location'].includes(prop),
})(({ device, isFloating, location }) => {
    return isFloating
        ? {
              position: 'fixed',
              zIndex: 3,
              width: device === 'mobile' ? '100%' : '300px',
              ...(location === 'grading' && {
                  ...(device === 'desktop' && {
                      bottom: '61px',
                      right: '40%',
                  }),
                  ...(device === 'tablet' && {
                      bottom: 'calc(55% - 61px)',
                      right: 0,
                  }),
                  ...(device === 'mobile' && {
                      bottom: 'calc(55% - 61px)',
                  }),
              }),
              ...(location === 'preview' && {
                  ...(device === 'desktop' && {
                      bottom: '76px',
                      right: 'calc(40% + 1px)',
                  }),
                  ...(device === 'tablet' && {
                      bottom: 'calc(50% + 4px)',
                      right: '5px',
                  }),
                  ...(device === 'mobile' && {
                      bottom: 'calc(48% - 1px)',
                      width: 'calc(100% - 10px)',
                  }),
              }),
              ...(location === 'submissions' && {
                  ...(device === 'desktop' && {
                      bottom: 0,
                      right: '40%',
                  }),
                  ...(device === 'tablet' && {
                      bottom: '47.25%',
                      right: 0,
                  }),
                  ...(device === 'mobile' && {
                      bottom: '47%',
                  }),
              }),
          }
        : {
              display: 'block',
              margin: 'auto',
              width: device === 'tablet' ? '50%' : '100%',
              aspectRatio: '16 / 9',
          };
});

export default VideoViewer;
